import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Stack
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import type { Subscription } from "../../Graphql/types/subscription";
import { dialogTitleCenterSx } from "../common/dialogTitleCenterSx";
import { useCancelSubscription } from "../../hooks/api/useSubscription";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

interface CancelSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription;
  onSuccess?: () => void;
}

/** Matches order-summary / marketing cards */
const MODAL_MAX_WIDTH_PX = 560;

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  onClose,
  subscription,
  onSuccess
}) => {
  const { t } = useKivunovaTranslation();
  const cancelSubscription = useCancelSubscription();

  const handleCancel = async () => {
    try {
      await cancelSubscription.mutateAsync({
        subscriptionId: subscription.subscriptionId
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by fetchGraphQL / setAlert
    }
  };

  const isPending = subscription.status === "PENDING";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="cancel-subscription-dialog-title"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: MODAL_MAX_WIDTH_PX,
          mx: 2,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(46, 31, 92, 0.16)"
        }
      }}
    >
      <Box
        sx={{
          height: 4,
          background:
            "linear-gradient(90deg, #667eea 0%, #764ba2 35%, #6f49c9 65%, #5b36b0 100%)"
        }}
      />
      <DialogTitle
        id="cancel-subscription-dialog-title"
        sx={{
          ...dialogTitleCenterSx,
          px: { xs: 2.5, sm: 3 },
          pt: 3,
          pb: 1
        }}
      >
        <Typography
          variant="h6"
          component="span"
          fontWeight={700}
          sx={{
            letterSpacing: "-0.02em",
            color: "text.primary",
            fontSize: { xs: "1.15rem", sm: "1.25rem" }
          }}
        >
          {t("cancel-subscription-modal-title", "Cancel subscription")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 1, pb: 0 }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1.75,
              borderLeft: "4px solid",
              borderLeftColor: isPending ? "warning.main" : BRAND_COLOR,
              bgcolor: isPending
                ? "rgba(255, 251, 235, 0.85)"
                : "rgba(247, 242, 255, 0.95)",
              borderTop: "1px solid",
              borderRight: "1px solid",
              borderBottom: "1px solid",
              borderColor: isPending
                ? "rgba(245, 158, 11, 0.22)"
                : "rgba(111, 73, 201, 0.18)"
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              {isPending ? (
                <PaymentOutlinedIcon
                  sx={{
                    color: "warning.dark",
                    fontSize: 22,
                    mt: 0.15,
                    opacity: 0.9
                  }}
                  aria-hidden
                />
              ) : (
                <InfoOutlinedIcon
                  sx={{
                    color: BRAND_COLOR,
                    fontSize: 22,
                    mt: 0.15,
                    opacity: 0.95
                  }}
                  aria-hidden
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  lineHeight: 1.6,
                  fontWeight: 500
                }}
              >
                {isPending
                  ? t(
                      "cancel-subscription-modal-pending-note",
                      "This subscription is pending payment. Cancelling it will close this request so you can start again."
                    )
                  : t(
                      "cancel-subscription-modal-active-until",
                      "Your subscription will remain active until {{date}}. You will continue to have access to all features until then.",
                      {
                        date: new Date(
                          subscription.subscriptionEndsAt
                        ).toLocaleDateString()
                      }
                    )}
              </Typography>
            </Stack>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.65,
              fontWeight: 400
            }}
          >
            {isPending
              ? t(
                  "cancel-subscription-modal-confirm-pending-message",
                  "Are you sure you want to cancel this pending subscription?"
                )
              : t(
                  "cancel-subscription-modal-confirm-message",
                  "Are you sure you want to cancel this subscription? You will lose access to premium features after the current billing period ends."
                )}
          </Typography>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pb: 3,
          pt: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
          width: "100%"
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{
            maxWidth: 400,
            py: 1.35,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.95rem",
            borderRadius: 2,
            backgroundColor: BRAND_COLOR,
            boxShadow: "0 4px 14px rgba(111, 73, 201, 0.35)",
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              boxShadow: "0 8px 20px rgba(111, 73, 201, 0.4)"
            }
          }}
        >
          {t("cancel-subscription-modal-keep-button", "Keep subscription")}
        </Button>
        <LoadingButton
          variant="text"
          fullWidth
          loading={cancelSubscription.isPending}
          onClick={handleCancel}
          sx={{
            maxWidth: 400,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "error.main",
            "&:hover": {
              bgcolor: "rgba(211, 47, 47, 0.06)"
            }
          }}
        >
          {t("cancel-subscription-modal-cancel-button", "Cancel subscription")}
        </LoadingButton>
      </Box>
    </Dialog>
  );
};

export default CancelSubscriptionModal;
